'use client';

/**
 * OverflowTooltip Component
 *
 * Wraps a single child and shows a tooltip only when the child's content is
 * clipped (typically by `text-overflow: ellipsis`). Pass `title` to override
 * the tooltip text; otherwise, the child must be plain text and that string is
 * used as the title.
 *
 * The wrapper renders as a flex span by default so the inner element keeps its
 * `min-width: 0` overflow context. The child is cloned with the measurement
 * ref attached, so it must be a single ref-forwarding element.
 *
 * @example
 * ```tsx
 * <OverflowTooltip>
 *   <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
 *     {longText}
 *   </Box>
 * </OverflowTooltip>
 * ```
 */

import Tooltip, { type TooltipProps } from '@mui/material/Tooltip';
import React, { cloneElement, isValidElement } from 'react';
import { useOverflowTooltip } from '../../hooks/useOverflowTooltip';

export interface OverflowTooltipProps extends Omit<TooltipProps, 'children' | 'title'> {
  /** The element whose content may be truncated. Must accept a ref. */
  children: React.ReactElement;
  /**
   * Tooltip content. If omitted and the child renders a plain string, that
   * string is used. Pass explicitly when the child has rich content but the
   * tooltip should show a different label.
   */
  title?: React.ReactNode;
  /** Which axis to measure. @default 'x' */
  axis?: 'x' | 'y';
  /** Disable the tooltip outright (still renders the child). */
  disabled?: boolean;
}

export const OverflowTooltip: React.FC<OverflowTooltipProps> = ({
  children,
  title,
  axis = 'x',
  disabled = false,
  placement = 'top',
  arrow = true,
  ...tooltipProps
}) => {
  const { ref, isOverflowing } = useOverflowTooltip<HTMLElement>({ axis, disabled });

  if (!isValidElement(children)) return children;

  // React 19 surfaces ref as a regular prop; React 18 also exposes it on the
  // element. Read from both so we can merge with whatever the child already had.
  const childProps = (children.props ?? {}) as { ref?: React.Ref<HTMLElement>; children?: React.ReactNode };
  const legacyRef = (children as unknown as { ref?: React.Ref<HTMLElement> }).ref;
  const existingRef = childProps.ref ?? legacyRef;
  const mergedRef: React.RefCallback<HTMLElement> = (node) => {
    ref(node);
    if (typeof existingRef === 'function') existingRef(node);
    else if (existingRef && typeof existingRef === 'object') {
      (existingRef as React.MutableRefObject<HTMLElement | null>).current = node;
    }
  };
  const child = cloneElement(children, { ref: mergedRef } as never);

  const resolvedTitle: React.ReactNode = title ?? (typeof childProps.children === 'string' ? childProps.children : '');

  if (disabled || !resolvedTitle) {
    return child;
  }

  // Using MUI Tooltip directly (not GlassTooltip) because GlassTooltip wraps
  // children in an `inline-flex` span, which collapses `min-width: 0` flex
  // ancestors and breaks `text-overflow: ellipsis` on the wrapped element.
  return (
    <Tooltip
      {...tooltipProps}
      title={resolvedTitle}
      placement={placement}
      arrow={arrow}
      disableHoverListener={!isOverflowing}
      disableFocusListener={!isOverflowing}
      disableTouchListener={!isOverflowing}
    >
      {child}
    </Tooltip>
  );
};

export default OverflowTooltip;
